/**
 * LeftShield Portal - Environments Page
 * Displays environment-related blind spots and placeholder sections for upcoming features
 * Route: /environments | Nav: Operations > Environments
 *
 * Blind-spots list migrated onto the List & Grid Standard — see
 * `.github/context/list-grid-standard.context.md` and
 * `.github/context/list-grid-standard-behaviours.context.md`. Reference migration:
 * `src/pages/ApiKeysPage.tsx`.
 */
import React, { useEffect } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  tokens,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@fluentui/react-components';
import {
  CloudRegular,
  PlugConnectedRegular,
  ArrowSyncRegular,
  ServerRegular,
  DocumentSearchRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { diagnosticApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import type { RuntimeBlindSpot } from '../types';
import {
  ListToolbar,
  Pager,
  useListUrlState,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';

const ENV_BLIND_SPOT_TYPES = ['ENV_NOT_SCANNED', 'IAC_DRIFT_DETECTED'];

const getSeverityColor = (severity: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (severity?.toLowerCase()) {
    case 'critical':
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'informative';
  }
};

const getStatusColor = (status: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (status) {
    case 'Open':
      return 'danger';
    case 'Acknowledged':
      return 'warning';
    case 'Mitigated':
    case 'Resolved':
      return 'success';
    default:
      return 'informative';
  }
};

const getTypeLabel = (type: string): string => {
  switch (type) {
    case 'ENV_NOT_SCANNED':
      return 'Environment Not Scanned';
    case 'IAC_DRIFT_DETECTED':
      return 'IaC Drift Detected';
    default:
      return type;
  }
};

const getTypeBadgeColor = (type: string): 'warning' | 'danger' | 'informative' => {
  switch (type) {
    case 'ENV_NOT_SCANNED':
      return 'warning';
    case 'IAC_DRIFT_DETECTED':
      return 'danger';
    default:
      return 'informative';
  }
};

/** Wraps the (unpaged) `diagnosticApi.getRuntimeBlindSpotsList` response — filtered down to the
 *  environment-related blind spot types — as a `PagedResult`. `useAdaptiveGridData` then applies
 *  search/sort/paging in memory (client mode), since this list is never large enough to warrant
 *  a real server-paged endpoint. */
async function fetchEnvBlindSpotsPage(params: ListQueryParams): Promise<PagedResult<RuntimeBlindSpot>> {
  const blindSpots = await diagnosticApi.getRuntimeBlindSpotsList();
  const items = blindSpots.filter((bs) => ENV_BLIND_SPOT_TYPES.includes(bs.blindSpotType));
  return {
    items,
    totalCount: items.length,
    pageNumber: 1,
    pageSize: params.pageSize,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
  };
}

export const EnvironmentsPage: React.FC = () => {
  const ps = usePageStyles();
  const navigate = useNavigate();

  const { state, setPage, setSort, setSearch } = useListUrlState({ defaultSort: 'lastSeenAt:desc' });

  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const sortState: SortState[] = [{ field: sortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const grid = useAdaptiveGridData<RuntimeBlindSpot>({
    queryKeyBase: ['runtimeBlindSpotsList', 'environment'],
    fetchPage: fetchEnvBlindSpotsPage,
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['scopeId', 'scopeType', 'recommendedAction'],
    sortState,
  });

  const {
    items: envBlindSpots,
    totalCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = grid;

  // Out-of-range clamp (§9) — never strand the user on an empty page after search/sort.
  useEffect(() => {
    if (!isLoading && state.page > totalPages) {
      setPage(totalPages);
    }
  }, [isLoading, state.page, totalPages, setPage]);

  let blindSpotsBody: React.ReactNode;
  if (isLoading) {
    blindSpotsBody = <LoadingSkeleton rows={6} columns={6} />;
  } else if (isError) {
    blindSpotsBody = <ErrorState message="Unable to load environment blind spots." onRetry={refetch} />;
  } else if (envBlindSpots.length === 0) {
    blindSpotsBody = (
      <EmptyState
        icon={<DocumentSearchRegular />}
        title="No Environment Blind Spots"
        description="No environments connected yet. Connect a cloud provider to begin monitoring."
        actionLabel="Connect Cloud Providers"
        onAction={() => navigate('/integrations')}
      />
    );
  } else {
    blindSpotsBody = (
      <ListLoadingOverlay busy={isFetching && !isLoading}>
        <Table aria-label="Environment blind spots">
          <TableHeader>
            <TableRow>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'severity' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('severity')}
                onClick={() => toggleSort('severity')}
              >
                Severity
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'blindSpotType' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('blindSpotType')}
                onClick={() => toggleSort('blindSpotType')}
              >
                Type
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'scopeId' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('scopeId')}
                onClick={() => toggleSort('scopeId')}
                style={{ width: '100%' }}
              >
                Scope
              </TableHeaderCell>
              <TableHeaderCell>Recommended Action</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'status' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('status')}
                onClick={() => toggleSort('status')}
              >
                Status
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'lastSeenAt' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('lastSeenAt')}
                onClick={() => toggleSort('lastSeenAt')}
              >
                Last Seen
              </TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {envBlindSpots.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Badge appearance="filled" color={getSeverityColor(item.severity)}>
                    {item.severity}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge appearance="tint" color={getTypeBadgeColor(item.blindSpotType)}>
                    {getTypeLabel(item.blindSpotType)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div>
                    <Body1 style={{ fontWeight: 500 }}>{item.scopeId}</Body1>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{item.scopeType}</Body2>
                  </div>
                </TableCell>
                <TableCell>
                  <Body2>{item.recommendedAction}</Body2>
                </TableCell>
                <TableCell>
                  <Badge appearance="filled" color={getStatusColor(item.status)}>
                    {item.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Body2>{new Date(item.lastSeenAt).toLocaleString()}</Body2>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={ps.page}>
      <PageHeader title="Environments" subtitle="Monitor cloud environments, detect infrastructure drift, and track resource inventory across your deployments" />

      {/* Environment-Related Blind Spots */}
      <Card>
        <CardHeader
          image={<DocumentSearchRegular style={{ fontSize: '1.5rem', color: '#E89652' }} />}
          header={<Title2>Environment Blind Spots</Title2>}
          description={
            <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
              Unscanned environments and detected infrastructure drift
            </Body2>
          }
        />
        <div className={ps.cardBody}>
          <ListToolbar
            search={state.search}
            onSearchChange={setSearch}
            searchPlaceholder="Search blind spots…"
            onRefresh={refetch}
            isRefreshing={isFetching}
            testIdPrefix="env-blind-spots"
          />
          {blindSpotsBody}
          <Pager
            page={state.page}
            totalPages={totalPages}
            hasPreviousPage={hasPreviousPage}
            hasNextPage={hasNextPage}
            totalCount={totalCount}
            onPageChange={setPage}
            testId="env-blind-spots-pager"
          />
        </div>
      </Card>

      {/* Planned Capabilities */}
      <div className={ps.metricGrid}>
        {/* Drift Detection */}
        <Card>
          <CardHeader
            image={<ArrowSyncRegular style={{ fontSize: '1.5rem', color: tokens.colorNeutralForeground3 }} />}
            header={<Title2>Drift Detection</Title2>}
            action={
              <Badge appearance="tint" color="informative">
                Planned
              </Badge>
            }
          />
          <div className={ps.cardBody}>
            <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
              Automatically detect configuration drift between your infrastructure-as-code definitions
              and deployed resources. Get notified when environments deviate from their intended state.
            </Body1>
          </div>
        </Card>

        {/* Cloud Resource Inventory */}
        <Card>
          <CardHeader
            image={<ServerRegular style={{ fontSize: '1.5rem', color: tokens.colorNeutralForeground3 }} />}
            header={<Title2>Cloud Resource Inventory</Title2>}
            action={
              <Badge appearance="tint" color="informative">
                Planned
              </Badge>
            }
          />
          <div className={ps.cardBody}>
            <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
              Maintain a real-time inventory of all cloud resources across your connected providers.
              Track resource counts, costs, and compliance status in a unified view.
            </Body1>
          </div>
        </Card>

        {/* Environment Comparison */}
        <Card>
          <CardHeader
            image={<CloudRegular style={{ fontSize: '1.5rem', color: tokens.colorNeutralForeground3 }} />}
            header={<Title2>Environment Comparison</Title2>}
            action={
              <Badge appearance="tint" color="informative">
                Planned
              </Badge>
            }
          />
          <div className={ps.cardBody}>
            <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
              Compare configurations across development, staging, and production environments.
              Identify discrepancies and ensure consistency across your deployment pipeline.
            </Body1>
          </div>
        </Card>
      </div>

      {/* CTA */}
      <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalL }}>
        <Button
          appearance="primary"
          size="large"
          icon={<PlugConnectedRegular />}
          onClick={() => navigate('/integrations')}
        >
          Connect Cloud Providers
        </Button>
      </div>
    </div>
  );
};
